// Namespace
import * as ProposalNS from '../Proposal';

// Utils
import { BMOi18n, GIF_TABLE_CONFIGS, pg1MaxRowsWithChart, pg2MaxRowsWithChart, pg3MaxRowsWithChart } from '../../../utils/bmo-gif/constants';
import { splitGifItemToPages } from '../../../utils/bmo-gif/utils';
import { getAge } from '../../../utils/time';
import { PROPOSAL_ORIENTATION } from '../utils/constants';
import Locale from '@/types/locale';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';
import BMOProposalData from '@/types/bmo-gif/bmo-proposal-data';

const buildBmoGifSection = (
  pageIndex: number,
  elements: ProposalNS.ProposalTemplateElement[],
  key: string,
  fraction: number,
): ProposalNS.ProposalTemplateSectionElement => {
  const newSection = {
    type: ProposalNS.ProposalTemplateElementType.Section,
    key: `${key}`,
    showTitle: false,
    sidebarControl: ProposalNS.ProposalSidebarControl.None,
    pageFraction: {
      Portrait: fraction,
      Landscape: fraction,
    },
    buildRules: [
      {
        type: ProposalNS.ElementBuildRuleTypes.GIFItems,
      },
      {
        type: ProposalNS.ElementBuildRuleTypes.Orientation,
        orientation: PROPOSAL_ORIENTATION.PORTRAIT,
      },
    ],
    label: {
      'en-CA': '',
      'fr-CA': '',
    },
    forced: true,
    layout: {
      type: ProposalNS.ProposalTemplateElementType.Layout,
      layout: {
        type: ProposalNS.LayoutType.Grid,
        columns: 1,
      },
      elements,
    },
  } as ProposalNS.ProposalTemplateSectionElement;

  return newSection;
};

const buildBmoGifSectionHeader = (locale: Locale, data: BMOProposalData): ProposalNS.ProposalTemplateDataElement => {
  let en = data.scenarioNumber !== undefined ? `${BMOi18n['en-CA'].SCENARIOS.SCENARIO} ${data.scenarioNumber}` : BMOi18n['en-CA'].PROPOSAL.BASE_SCENARIO;
  let fr = data.scenarioNumber !== undefined ? `${BMOi18n['fr-CA'].SCENARIOS.SCENARIO} ${data.scenarioNumber}` : BMOi18n['fr-CA'].PROPOSAL.BASE_SCENARIO;
  if (data.isAdverse) {
    en = `${en} - ${BMOi18n[locale].SCENARIOS.ADVERSE_SCENARIO}`;
    fr = `${fr} - ${BMOi18n[locale].SCENARIOS.ADVERSE_SCENARIO}`;
  }

  const element = {
    type: ProposalNS.ProposalTemplateElementType.Data,
    dataView: {
      type: ProposalNS.DataView.Text,
      className: 'base-scenario-header',
    },
    DAO: {
      type: ProposalNS.DAOType.Text,
      localizedTextDAOOptions: {
        texts: [
          {
            text: {
              'en-CA': en,
              'fr-CA': fr,
            },
          },
        ],
      },
    },
  } as ProposalNS.ProposalTemplateDataElement;

  return element;
};

const buildBmoGifGraph = (
  locale: Locale,
  pageIndex: number,
  fullPage: boolean,
  data: BMOProposalData,
  dob: Date,
): ProposalNS.ProposalTemplateSectionElement => {
  const elements = [
    buildBmoGifSectionHeader(locale, data),
    {
      type: ProposalNS.ProposalTemplateElementType.Data,
      dataView: {
        type: ProposalNS.DataView.Grid,
        columns: 5,
        className: 'gif-chart-legend',
      },
      DAO: {
        type: ProposalNS.DAOType.BmoGif,
        pageIndex,
        isGraph: true,
      },
    },
    {
      type: ProposalNS.ProposalTemplateElementType.Data,
      dataView: {
        type: ProposalNS.DataView.Line,
        configOptions: {
          yAxis: {
            labels: {
              y: -8,
            },
            gridLineWidth: 1,
            opposite: true,
            showLastLabel: false,
          },
          xAxis: {
            labels: {
              formatter(this: any) {
                const age = getAge(dob, undefined, new Date(this.value));
                return `${new Date(this.value).getUTCFullYear()}<br>${age}`;
              },
            },
            gridLineWidth: 1,
          },
          chart: {
            height: 600,
          },
          tooltip: {
            enabled: true,
          },
        },
      },
      DAO: {
        type: ProposalNS.DAOType.BmoGif,
        pageIndex,
        isGraph: true,
      },
    },
  ] as ProposalNS.ProposalTemplateElement[];

  return buildBmoGifSection(pageIndex, elements, 'BMO_GIF_GRAPH', fullPage ? 1 : 0.45);
};

// Shows the Bmo Gif Summary Table only on first page of scenario pages and hides on second page
const showBmoGifSummaryTable = (pageIndex: number, data: BMOProposalData) => {
  let showTable = {};

  if (data.scenarioPageIndex === 1) {
    showTable = {
      type: ProposalNS.ProposalTemplateElementType.Data,
      dataView: {
        type: ProposalNS.DataView.Grid,
        className: 'bmo-gif-scenario-summary',
        columns: 3,
      },
      DAO: {
        type: ProposalNS.DAOType.BmoGif,
        pageIndex,
        isScenarioSummary: true,
      },
    };
  }

  return showTable;
};

const buildBmoGifTable = (locale: Locale, pageIndex: number, data: BMOProposalData): ProposalNS.ProposalTemplateSectionElement => {
  const messages = BMOi18n[locale];

  const elements = [
    buildBmoGifSectionHeader(locale, data),
    showBmoGifSummaryTable(pageIndex, data),
    {
      type: ProposalNS.ProposalTemplateElementType.Data,
      dataView: {
        type: ProposalNS.DataView.Table,
        className: 'bmo-gif-chart-legend',
      },
      DAO: {
        type: ProposalNS.DAOType.BmoGif,
        pageIndex,
        isGraph: false,
        customColumnNamesDAOOptions: {
          columns: [
            {
              name: {
                'en-CA': messages.PROPOSAL.HEADERS.DATE,
                'fr-CA': messages.PROPOSAL.HEADERS.DATE,
              },
            },
            {
              name: {
                'en-CA': messages.AGE,
                'fr-CA': messages.AGE,
              },
              color: GIF_TABLE_CONFIGS.AGE.color,
            },
            {
              name: {
                'en-CA': messages.SCENARIOS.PORTFOLIO_VALUE,
                'fr-CA': messages.SCENARIOS.PORTFOLIO_VALUE,
              },
              color: GIF_TABLE_CONFIGS.PORTFOLIO_VALUE.color,
            },
            {
              name: {
                'en-CA': messages.SCENARIOS.NET_WITHDRAWAL,
                'fr-CA': messages.SCENARIOS.NET_WITHDRAWAL,
              },
              color: GIF_TABLE_CONFIGS.NET_WITHDRAWAL.color,
            },
            {
              name: {
                'en-CA': messages.SCENARIOS.SUBSEQUENT_DEPOSIT,
                'fr-CA': messages.SCENARIOS.SUBSEQUENT_DEPOSIT,
              },
              color: GIF_TABLE_CONFIGS.SUBSEQUENT_DEPOSIT.color,
            },
            {
              name: {
                'en-CA': messages.SCENARIOS.MATURITY_GUARANTEE,
                'fr-CA': messages.SCENARIOS.MATURITY_GUARANTEE,
              },
              color: GIF_TABLE_CONFIGS.MATURITY_GUARANTEE.color,
            },
            {
              name: {
                'en-CA': messages.SCENARIOS.DEATH_BENEFIT_GUARANTEE,
                'fr-CA': messages.SCENARIOS.DEATH_BENEFIT_GUARANTEE,
              },
              color: GIF_TABLE_CONFIGS.DEATH_BENEFIT_GUARANTEE.color,
            },
          ],
        },
      },
    },
  ] as ProposalNS.ProposalTemplateElement[];

  return buildBmoGifSection(pageIndex, elements, 'BMO_GIF_TABLE', 0.51);
};

const showChart = (data: BMOProposalData): boolean => {
  const { scenarioPageIndex: page, pageData } = data;

  if (page === 1) {
    return pageData.length <= pg1MaxRowsWithChart;
  }

  if (page === 2) {
    return pageData.length <= pg2MaxRowsWithChart;
  }

  if (page === 3) {
    return pageData.length <= pg3MaxRowsWithChart;
  }

  // Always return graph on last page
  return true;
};

export const buildBmoGifSections = (item: BMOGIFItem, locale: Locale, hideBaseScenario: boolean): ProposalNS.ProposalTemplateSectionElement[] => {
  const newSections: ProposalNS.ProposalTemplateSectionElement[] = [];
  const pages: BMOProposalData[] = splitGifItemToPages(item, hideBaseScenario);

  for (let i = 0; i < pages.length; i++) {
    const data: BMOProposalData = pages[i];
    let fullPage: boolean = true;

    if (data.pageData.length > 0) {
      const tableSection: ProposalNS.ProposalTemplateSectionElement = buildBmoGifTable(locale, i, data);
      newSections.push(tableSection);
      fullPage = false;
    }

    if (showChart(data)) {
      const newGraph: ProposalNS.ProposalTemplateSectionElement = buildBmoGifGraph(locale, i, fullPage, data, item.portfolio.dob);
      newSections.push(newGraph);
    }
  }

  return newSections;
};
